import { useEffect } from 'react';
import Header from '../../components/feature/Header';

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: 'ri-building-line',
      title: 'Construction Services',
      description: 'Comprehensive construction solutions for commercial, residential, and industrial projects. From planning to execution, we deliver excellence at every stage.',
      features: [
        'Commercial Building Construction',
        'Residential Complex Development',
        'Industrial Facility Construction',
        'Renovation and Remodeling',
      ],
      image: 'https://public.readdy.ai/ai/img_res/0f0225f05219ac8bd43d6b1dc961bb74.jpg',
    },
    {
      icon: 'ri-truck-line',
      title: 'Trading & Supply',
      description: 'Reliable trading solutions for construction materials, equipment, and industrial supplies. We ensure quality products and timely delivery for all your project needs.',
      features: [
        'Construction Materials Supply',
        'Heavy Equipment Trading',
        'Industrial Tools & Machinery',
        'Quality Assurance & Certification',
      ],
      image: 'https://readdy.ai/api/search-image?query=organized%20warehouse%20with%20construction%20materials%20steel%20beams%20pipes%20equipment%20inventory%20forklift%20operations%20professional%20logistics%20clean%20industrial%20environment%20bright%20lighting%20minimalist%20background&width=800&height=600&seq=raissy-service-trading-702&orientation=landscape',
    },
    {
      icon: 'ri-road-map-line',
      title: 'Infrastructure Development',
      description: 'Large-scale infrastructure projects including roads, bridges, and utilities. We build the foundations that connect and empower communities.',
      features: [
        'Road and Highway Construction',
        'Bridge and Tunnel Projects',
        'Utility Infrastructure',
        'Site Development & Preparation',
      ],
      image: 'https://readdy.ai/api/search-image?query=highway%20bridge%20construction%20infrastructure%20development%20civil%20engineering%20concrete%20pillars%20steel%20reinforcement%20modern%20road%20building%20heavy%20machinery%20bright%20daylight%20clean%20minimalist%20background&width=800&height=600&seq=raissy-service-infrastructure-703&orientation=landscape',
    },
    {
      icon: 'ri-file-list-line',
      title: 'Contracting Services',
      description: 'Professional contracting services with experienced teams managing every aspect of your project from start to finish with precision and expertise.',
      features: [
        'General Contracting',
        'Subcontracting Management',
        'Quality Control & Inspection',
        'Safety Management',
      ],
      image: 'https://readdy.ai/api/search-image?query=construction%20team%20reviewing%20blueprints%20at%20building%20site%20professional%20contractors%20engineers%20with%20plans%20safety%20equipment%20modern%20project%20coordination%20bright%20clean%20minimalist%20background&width=800&height=600&seq=raissy-service-contracting-704&orientation=landscape',
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Project Management',
      description: 'End-to-end project management services ensuring your project is delivered on time, within budget, and to the highest quality standards.',
      features: [
        'Project Planning & Scheduling',
        'Budget Management',
        'Resource Allocation',
        'Risk Management',
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20business%20meeting%20construction%20project%20planning%20architectural%20blueprints%20engineering%20drawings%20modern%20office%20environment%20team%20collaboration%20bright%20clean%20minimalist%20background&width=800&height=600&seq=raissy-service-management-705&orientation=landscape',
    },
    {
      icon: 'ri-tools-line',
      title: 'Maintenance & Support',
      description: 'Comprehensive maintenance and support services to ensure the longevity and optimal performance of your facilities and infrastructure.',
      features: [
        'Preventive Maintenance',
        'Emergency Repairs',
        'Facility Management',
        'Technical Support',
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20maintenance%20technicians%20inspecting%20building%20systems%20equipment%20tools%20modern%20facility%20management%20operations%20bright%20clean%20minimalist%20background&width=800&height=600&seq=raissy-service-maintenance-706&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=impressive%20construction%20site%20aerial%20view%20multiple%20projects%20cranes%20equipment%20workers%20coordinating%20professional%20operations%20modern%20development%20bright%20daylight%20clean%20minimalist%20background&width=1920&height=800&seq=raissy-services-hero-707&orientation=landscape"
            alt="Our Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Comprehensive Solutions for All Your Construction and Trading Needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-12 max-w-[1140px]">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">
              What We Offer
            </h2>
            <p className="text-lg text-[#4a4a4a] max-w-3xl mx-auto">
              From construction to trading, we provide end-to-end solutions tailored to meet your specific requirements
            </p>
          </div>

          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-16 h-16 bg-[#0891b2]/10 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-[#0891b2]`}></i>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
                    {service.title}
                  </h3>
                  <p className="text-lg text-[#4a4a4a] mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-check-line text-[#0891b2] text-xl mr-3 mt-1"></i>
                        <span className="text-[#4a4a4a]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/contact"
                    className="inline-block border-2 border-[#0891b2] text-[#0891b2] px-8 py-3 rounded-md text-sm font-semibold hover:bg-[#0891b2] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Learn More
                  </a>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#f5f5f5]">
        <div className="container mx-auto px-8 lg:px-12 max-w-[1140px]">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">
              Why Choose Raissy?
            </h2>
            <p className="text-lg text-[#4a4a4a] max-w-3xl mx-auto">
              We combine experience, expertise, and innovation to deliver exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-medal-line',
                title: 'Proven Track Record',
                description: 'Over 25 years of successful project delivery across various sectors',
              },
              {
                icon: 'ri-team-line',
                title: 'Expert Team',
                description: 'Highly skilled professionals with extensive industry experience',
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Competitive Pricing',
                description: 'Quality services at competitive rates with transparent pricing',
              },
              {
                icon: 'ri-time-line',
                title: 'On-Time Delivery',
                description: 'Commitment to meeting deadlines without compromising quality',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Quality Assurance',
                description: 'Rigorous quality control processes at every project stage',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: '24/7 Support',
                description: 'Round-the-clock customer support for all your needs',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-[#0891b2]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-2xl text-[#0891b2]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#4a4a4a] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0891b2]">
        <div className="container mx-auto px-8 lg:px-12 max-w-[1140px] text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Build Something Great Together
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your project requirements and discover how we can help
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-[#0891b2] px-10 py-4 rounded-md text-base font-semibold hover:bg-[#f5f5f5] transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Started
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
